import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import JSZip from "jszip";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { IconCalendar } from "@tabler/icons-react";
import DatePicker from "react-datepicker";
import {
  IonIcon,
  isPlatform,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  useIonToast,
} from "@ionic/react";
import { closeCircle } from "ionicons/icons";
import { IconDownload } from "@tabler/icons-react";

// Components
import SkeletonText from "./../../SkeletonText";
import SkeletonLoader from "./../../SkeletonLoader";
import { Filter } from "../components/filters";
import FilterPill from "../components/filter-pill";
import Table from "./table";
import Modal from "./modal";
import List from "./list";

// Interfaces
import { Settings, PaymentsFilter } from "../../../interfaces";

// Utils
import { parseDateForFilter } from "./../../../utils/moment";

// Api
import LeadsApi from "../../../api/leads";

// Store
import { usePaymentsStore } from "./../../../store/payments";

// Styles
import layoutStyles from "../layout.module.css";

const isMobile = isPlatform("android") || isPlatform("ios");

const LayoutSkeleton = () => (
  <div className="row">
    <div className="col-md-12">
      <div className="flex flex-column w-100 h-100">
        <SkeletonText rows={20} />
      </div>
    </div>
  </div>
);

const download = (blob: Blob, filename = "data.csv") => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const escapeCSVValue = (value: any): string => {
  const stringValue = String(value ?? ""); // Maneja null/undefined
  const escaped = stringValue.replace(/"/g, '""'); // Escapa comillas dobles
  return `"${escaped}"`; // Encierra todo entre comillas dobles
};

const Payments: React.FC<ILayoutProps> = (props: ILayoutProps) => {
  const intl = useIntl();

  const { locale } = intl;
  const history = useHistory();
  const [present] = useIonToast();
  const { setPayments } = usePaymentsStore();
  const [isOpen, setIsOpen] = useState(false);
  const [payment, setPayment] = useState<any>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [dateQuery, setDateQuery] = useState("");
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [filters, setFilters] = useState<PaymentsFilter>({
    companies: [],
    statuses: [],
  });
  const queryClient = useQueryClient();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteQuery({
    queryKey: ["getLeads", dateQuery, filters],
    queryFn: ({
      pageParam,
    }: {
      pageParam?: { cursor: string | null; direction: string | null };
    }) => {
      const { cursor = null, direction = null } = pageParam ?? {};
      return LeadsApi.getPayments({
        date: dateQuery,
        cursor,
        direction,
        filters,
      });
    },
    refetchOnWindowFocus: false,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    getPreviousPageParam: (_, pages) => pages[pages.length - 1].previousCursor,
  });

  const { isLoading, data: totalsData } = useQuery({
    queryFn: () => LeadsApi.getPaymentsTotals(),
    queryKey: ["getPaymentsTotals"],
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data) {
      setPayments(
        !isMobile
          ? data?.pages[data?.pages.length - 1].payments
          : data?.pages.flatMap((page) => page.payments)
      );
    }
  }, [data]);

  const handleExport = async () => {
    setExportLoading(true);
    const { payments } = await LeadsApi.getPaymentsExport();
    setExportLoading(false);
    const zip = new JSZip();
    if (payments?.length) {
      const rows = payments.map((row: any[]) =>
        row.map(escapeCSVValue).join(",")
      );
      zip.file("leads.csv", rows.join("\n"));
    }

    const blob = await zip.generateAsync({ type: "blob" });
    download(blob, "export-payments.zip");
  };

  const onChangeDates = (dates: [Date, Date]) => {
    const [start, end] = dates;
    if (start && end) {
      setDateQuery(
        () => `${parseDateForFilter(start)},${parseDateForFilter(end)}`
      );
    }
    setStartDate(start);
    setEndDate(end);
  };

  const handleTransfFunds = async (payment: any) => {
    if (payment?.id) {
      const { data, err } = await LeadsApi.postTransferFunds({
        paymentId: payment?.id,
      });
      if (data) {
        return present({
          message: intl.formatMessage({ id: "common.success" }),
          duration: 10000,
          color: "success",
          position: "top",
        });
      }
      return present({
        message: err.err,
        duration: 10000,
        color: "danger",
        position: "top",
      });
    }
  };

  const openPaymentModal = (payment: any) => {
    // Logic to open payment modal
    setIsOpen(true);
    setPayment(payment);
  };

  const openConversation = (payment: any) => {
    const contactId = payment.contact?.id;

    if (!contactId) {
      return present({
        message: intl.formatMessage({ id: "common.error" }),
        duration: 10000,
        color: "danger",
        position: "top",
      });
    }
    if (isMobile) {
      history.push(`/chat/${encodeURIComponent(contactId)}`);
    } else {
      history.push(`/chats/${encodeURIComponent(contactId)}`);
    }
  };

  const filterPillTotal = (
    <FilterPill
      isMobile={isMobile}
      name={intl.formatMessage({ id: "leads.stats.total" })}
      count={totalsData?.totalCount}
      active={
        filters.statuses.length === 0 && startDate === null && endDate === null
      }
      onClick={() => {
        setFilters({
          statuses: [],
          companies: [],
        });
        setDateQuery("");
        setStartDate(null);
        setEndDate(null);
      }}
    />
  );

  const filterPillPaid = (
    <FilterPill
      isMobile={isMobile}
      name={intl.formatMessage({ id: `leads.payments.paid` })}
      count={totalsData?.paidTotal}
      active={filters.statuses.length === 1 && filters.statuses[0] === "paid"}
      onClick={() => {
        setFilters({
          ...filters,
          statuses: ["paid"],
          companies: [],
        });
        setDateQuery("");
        setStartDate(null);
        setEndDate(null);
      }}
    />
  );

  const { nextCursor, previousCursor } =
    data?.pages[data.pageParams.length - 1] || {};

  return isMobile ? (
    <>
      <div className={`${layoutStyles.filtersWrapper} pt-2`}>
        {filterPillTotal}
        {filterPillPaid}
      </div>
      {isFetching && !isFetchingNextPage ? (
        <SkeletonLoader rows={10} />
      ) : (
        <>
          <List
            handleClick={(payment: any) => {
              openPaymentModal(payment);
            }}
            settings={props.settings}
          />
          <IonInfiniteScroll
            disabled={!hasNextPage}
            onIonInfinite={(event) => {
              fetchNextPage({
                pageParam: { direction: "next", cursor: nextCursor },
              }).then(() => {
                event.target.complete();
              });
            }}
          >
            <IonInfiniteScrollContent></IonInfiniteScrollContent>
          </IonInfiniteScroll>
        </>
      )}
      <Modal
        isOpen={isOpen}
        onWillDismiss={() => {
          setIsOpen(false);
          setPayment(null);
        }}
        selectedPayment={payment}
      />
    </>
  ) : (
    <>
      <div className="row mt-3 mb-3">
        <div className={`col-md-2`}>{filterPillTotal}</div>
        <div className={`col-md-2`}>{filterPillPaid}</div>
      </div>
      <div className="flex gap-1 align-items-center justify-content-between">
        <div className="flex gap-1 align-items-center mb-3">
          <div className={`${layoutStyles.filters}`}>
            <div className={layoutStyles.dateWrapper}>
              <IconCalendar size={17} />
              <DatePicker
                selectsRange
                locale={locale}
                startDate={startDate}
                endDate={endDate}
                className={
                  startDate || endDate
                    ? layoutStyles.dateInput
                    : layoutStyles.dateInputEmpty
                }
                placeholderText={intl.formatMessage({
                  id: "leads.datePlaceholder",
                })}
                onChange={onChangeDates}
                monthsShown={2}
                popperPlacement="bottom-end"
              />
            </div>
          </div>
          {startDate && endDate ? (
            <IonIcon
              style={{ opacity: "50%", marginLeft: "3px" }}
              icon={closeCircle}
              className="pointer"
              size="small"
              onClick={() => {
                setStartDate(null);
                setEndDate(null);
                setDateQuery("");
              }}
            ></IonIcon>
          ) : null}

          {props.settings.companies.length > 1 ? (
            <Filter
              options={props.settings.companies}
              onFilterChange={(filter: string[]) =>
                setFilters({ ...filters, companies: filter })
              }
              name={intl.formatMessage({ id: "common.company" })}
              currentFilters={filters.companies}
            ></Filter>
          ) : null}

          <Filter
            options={[
              {
                id: "paid",
                name: intl.formatMessage({ id: "leads.payments.paid" }),
              },

              {
                id: "expired",
                name: intl.formatMessage({ id: "leads.payments.expired" }),
              },
            ]}
            onFilterChange={(filter: string[]) =>
              setFilters({ ...filters, statuses: filter })
            }
            name={intl.formatMessage({ id: "common.status" })}
            currentFilters={filters.companies}
          ></Filter>
        </div>
        <div className="flex gap-1 align-items-center mb-3">
          <div className={layoutStyles.btnNavigation}>
            <button
              disabled={exportLoading}
              className={`btn btn-visito-light btn-sm btn-flex`}
              onClick={() => handleExport()}
            >
              <IconDownload size={14} />
              <div>
                {exportLoading ? (
                  <FormattedMessage id={`common.loading`} />
                ) : (
                  <FormattedMessage id={`leads.export`} />
                )}
              </div>
            </button>{" "}
          </div>
        </div>
      </div>
      {isFetching || isFetchingNextPage ? (
        <LayoutSkeleton />
      ) : (
        <div className={layoutStyles.tableContainer}>
          <Table
            settings={props.settings}
            handleClick={(payment: any) => {
              openPaymentModal(payment);
            }}
            onOpenConversation={(payment: any) => {
              openConversation(payment);
            }}
            onTransferFunds={(payment: any) => {
              handleTransfFunds(payment);
            }}
          />
        </div>
      )}
      <div className={layoutStyles.paginationWrapper}>
        <div className="flex gap-1 justify-content-end">
          <button
            disabled={!hasPreviousPage || isFetching || isFetchingPreviousPage}
            className="btn btn-visito-light btn-sm"
            onClick={() =>
              fetchNextPage({
                pageParam: { direction: "previous", cursor: previousCursor },
              })
            }
          >
            <FormattedMessage id="common.previous" />
          </button>
          <button
            disabled={
              !hasNextPage || isFetchingNextPage || isFetchingPreviousPage
            }
            className="btn btn-visito-light btn-sm"
            onClick={() =>
              fetchNextPage({
                pageParam: { direction: "next", cursor: nextCursor },
              })
            }
          >
            <FormattedMessage id="common.next" />
          </button>
        </div>
      </div>
      <Modal
        isOpen={isOpen}
        onWillDismiss={() => {
          setIsOpen(false);
          setPayment(null);
        }}
        selectedPayment={payment}
      />
      <br />
      <br />
      <br />
      <br />
    </>
  );
};

interface ILayoutProps {
  settings: Settings;
}

export default Payments;
